import React from "react";
import { Unavailable } from "./FootballPitch";
import { cumulativeXg } from "./fiData";
import "./fi-extra.css";

/**
 * Cumulative xG by minute for both teams, built only from timed provider shots.
 * Goal markers join to located shots by id; if the join finds none, no markers
 * are drawn (nothing is inferred from the final score).
 */
export default function XgTimeline({ timeline = [], shots = [], home, away }) {
  if (!timeline.length) {
    return <Unavailable text="No timed source xG events; a timeline cannot be inferred." />;
  }

  const lastOf = (list) => (list && list.length ? list[list.length - 1] : undefined);
  const teamIds = [home?.id, away?.id];
  const { series, lastMinute } = cumulativeXg(timeline, teamIds, shots);
  const maxTotal = Math.max(0.5, ...teamIds.map((id) => lastOf(series[id])?.total ?? 0));

  const W = 720;
  const H = 280;
  const m = { l: 38, r: 96, t: 16, b: 30 };
  const x = (minute) => m.l + (minute / lastMinute) * (W - m.l - m.r);
  const y = (xg) => H - m.b - (xg / (maxTotal * 1.1)) * (H - m.t - m.b);

  const stepPath = (points) => {
    let path = `M${x(0)} ${y(0)}`;
    let previous = 0;
    points.forEach((p) => {
      path += ` H${x(p.minute).toFixed(1)} V${y(p.total).toFixed(1)}`;
      previous = p.total;
    });
    path += ` H${x(lastMinute).toFixed(1)}`;
    return { d: path, end: previous };
  };

  const rows = [
    { team: home, cls: "a", points: series[home?.id] || [] },
    { team: away, cls: "b", points: series[away?.id] || [] },
  ];
  const yTicks = [0, maxTotal / 2, maxTotal].map((v) => Number(v.toFixed(2)));
  const xTicks = [0, 15, 30, 45, 60, 75, 90].filter((t) => t <= lastMinute);
  const goalCount = rows.reduce((n, r) => n + r.points.filter((p) => p.goal).length, 0);

  return (
    <div>
      <div className="fx-key">
        <span className="a">● {home?.name}</span>
        <span className="b">{away?.name} ●</span>
      </div>
      <svg className="fx-timeline" viewBox={`0 0 ${W} ${H}`} role="img"
        aria-label={`Cumulative expected goals: ${rows.map((r) => `${r.team?.name} ${(lastOf(r.points)?.total ?? 0).toFixed(2)}`).join(", ")}`}>
        {yTicks.map((t) => (
          <g key={t}>
            <line className="axis" x1={m.l} x2={W - m.r} y1={y(t)} y2={y(t)} />
            <text x={m.l - 6} y={y(t) + 4} textAnchor="end">{t}</text>
          </g>
        ))}
        {xTicks.map((t) => (
          <g key={t}>
            <line className="axis" x1={x(t)} x2={x(t)} y1={H - m.b} y2={H - m.b + 4} />
            <text x={x(t)} y={H - 10} textAnchor="middle">{t}′</text>
          </g>
        ))}
        {lastMinute >= 45 && <line className="axis" x1={x(45)} x2={x(45)} y1={m.t} y2={H - m.b} strokeDasharray="3 4" />}
        {rows.map(({ team, cls, points }) => {
          const { d, end } = stepPath(points);
          return (
            <g key={team?.id || cls}>
              <path className={cls} d={d} />
              {points.filter((p) => p.goal).map((p) => (
                <circle key={p.id} className={`goal-${cls}`} cx={x(p.minute)} cy={y(p.total)} r="5.5">
                  <title>{`${team?.name} goal · ${p.minute}′ · shot xG ${p.xg.toFixed(2)} · cumulative ${p.total.toFixed(2)}`}</title>
                </circle>
              ))}
              <text x={x(lastMinute) + 6} y={y(end) + 4} style={{ fill: cls === "a" ? "var(--fx-home)" : "var(--fx-away)", fontWeight: 700 }}>
                {end.toFixed(2)} xG
              </text>
            </g>
          );
        })}
      </svg>
      <p className="fi-note">
        Step lines add each timed shot's source xG. {goalCount ? `${goalCount} filled markers are goals matched by shot id.` : "No goal markers: no timed shot matched a located goal."} Stoppage-time shots extend the axis.
      </p>

      <details>
        <summary style={{ cursor: "pointer" }}>Shot-by-shot table</summary>
        <div className="fx-scroll" style={{ marginTop: 10 }}>
          <table>
            <thead><tr><th>Minute</th><th>Team</th><th className="fx-num">Shot xG</th><th className="fx-num">Cumulative xG</th></tr></thead>
            <tbody>
              {[...timeline].sort((a, b) => a.minute - b.minute).map((shot) => {
                const own = series[shot.team_id]?.find((p) => p.id === shot.id);
                return (
                  <tr key={shot.id}>
                    <td>{shot.minute}′</td>
                    <td>{shot.team_id === home?.id ? "Home" : "Away"}</td>
                    <td className="fx-num">{shot.xg.toFixed(2)}</td>
                    <td className="fx-num">{own ? own.total.toFixed(2) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
